import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Smartphone, Plus, Trash2, Save, Loader2, ImagePlus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const storageLabel = (gb) => (gb >= 1024 ? `${gb / 1024}TB` : `${gb}GB`);
const empty = { name: "", category: "", storages: "", base_price: "", image_url: "", has_faceid: true, has_esim: true, colors: "" };

export const ModelsManager = ({ onChanged }) => {
  const [models, setModels] = useState([]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editedSP, setEditedSP] = useState({});
  const [editedEsim, setEditedEsim] = useState({});
  const [editedDiff, setEditedDiff] = useState({});
  const fileRef = useRef(null);

  const load = () => api.get("/catalog").then((r) => setModels(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
    } catch { toast.error("Şəkil yüklənmədi"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const add = async () => {
    if (!form.name.trim() || !form.base_price) { toast.error("Ad və qiymət tələb olunur"); return; }
    const storages = form.storages.split(",").map((s) => parseInt(s.trim())).filter(Boolean);
    setSaving(true);
    try {
      await api.post("/admin/models", {
        name: form.name, category: form.category, storages: storages.length ? storages : [128],
        base_price: parseInt(form.base_price), image_url: form.image_url || null,
        has_faceid: form.has_faceid,
        has_esim: form.has_esim,
        colors: form.colors.split(",").map((c) => c.trim()).filter(Boolean),
      });
      setForm(empty);
      await load(); onChanged?.();
      toast.success("Model əlavə edildi");
    } catch { toast.error("Xəta baş verdi"); }
    finally { setSaving(false); }
  };

  const setSP = (mid, s, val) => setEditedSP((e) => ({ ...e, [mid]: { ...(e[mid] || {}), [s]: val } }));

  const savePrice = async (m) => {
    const cur = editedSP[m.model_id] || {};
    const merged = {};
    m.storages.forEach((s) => {
      const v = cur[s] ?? (m.storage_prices?.[String(s)] ?? m.base_price);
      merged[String(s)] = parseInt(v) || 0;
    });
    const isWatch = !m.storages || m.storages.length === 0;
    await api.put(`/admin/models/${m.model_id}`, {
      storage_prices: merged,
      base_price: isWatch ? (parseInt(editedSP[m.model_id]?.base ?? m.base_price) || 0) : Math.min(...Object.values(merged)),
      esim_diff: parseInt(editedEsim[m.model_id] ?? (m.esim_diff ?? 0)) || 0,
      no_reg_diff: parseInt(editedDiff[m.model_id]?.no_reg_diff ?? (m.no_reg_diff ?? 0)) || 0,
      no_beyan_diff: parseInt(editedDiff[m.model_id]?.no_beyan_diff ?? (m.no_beyan_diff ?? 0)) || 0,
      no_wifi_diff: parseInt(editedDiff[m.model_id]?.no_wifi_diff ?? (m.no_wifi_diff ?? 0)) || 0,
    });
    setEditedSP((e) => { const n = { ...e }; delete n[m.model_id]; return n; });
    setEditedEsim((e) => { const n = { ...e }; delete n[m.model_id]; return n; });
    setEditedDiff((e) => { const n = { ...e }; delete n[m.model_id]; return n; });
    load(); onChanged?.();
    toast.success("Qiymətlər yeniləndi");
  };

  const remove = async (id) => {
    await api.delete(`/admin/models/${id}`);
    load(); onChanged?.();
    toast.success("Model silindi");
  };

  return (
    <div className="space-y-6" data-testid="models-manager">
      {/* add form */}
      <div className="glass rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4 text-sm font-medium"><Plus className="w-4 h-4" /> Yeni model əlavə et</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Ad (məs. iPhone 17)" data-testid="model-name" className="bg-white/5 border-white/10" />
          <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            placeholder="Kateqoriya" data-testid="model-category" className="bg-white/5 border-white/10" />
          <Input value={form.storages} onChange={(e) => setForm({ ...form, storages: e.target.value })}
            placeholder="Yaddaş: 128,256,512" data-testid="model-storages" className="bg-white/5 border-white/10" />
          <Input value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })}
            placeholder="Rənglər: Qara,Ağ,Mavi" data-testid="model-colors" className="bg-white/5 border-white/10" />
          <Input value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })}
            placeholder="Baza qiymət (₼)" type="number" data-testid="model-price" className="bg-white/5 border-white/10" />
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} />
            {form.image_url ? (
              <div className="relative">
                <img src={fileUrl(form.image_url)} alt="" className="w-11 h-11 rounded-lg object-contain bg-white/5 border border-white/10" />
                <button onClick={() => setForm({ ...form, image_url: "" })} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-black border border-white/20 flex items-center justify-center">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()} data-testid="model-image-upload"
                className="h-11 px-3 rounded-lg border border-white/10 text-xs text-neutral-400 hover:text-white hover:border-white/30 transition-colors flex items-center gap-1.5">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} İkon
              </button>
            )}
            <button onClick={add} disabled={saving} data-testid="model-add-btn"
              className="h-11 flex-1 rounded-lg bg-neutral-100 text-black text-sm font-medium disabled:opacity-50 hover:bg-white transition-colors flex items-center justify-center gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Əlavə et
            </button>
          </div>
        </div>
        <button onClick={() => setForm({ ...form, has_faceid: !form.has_faceid })} data-testid="model-faceid"
          className={`mt-3 px-4 py-2 rounded-lg text-xs border transition-colors ${form.has_faceid ? "border-white/70 bg-white/[0.06] text-white" : "border-white/10 text-neutral-400"}`}>
          Face ID {form.has_faceid ? "var" : "yoxdur"}
        </button>
        <button onClick={() => setForm({ ...form, has_esim: !form.has_esim })} data-testid="model-hasesim"
          className={`mt-3 ml-2 px-4 py-2 rounded-lg text-xs border transition-colors ${form.has_esim ? "border-[#2997FF]/60 bg-[#2997FF]/10 text-[#7fbcff]" : "border-white/10 text-neutral-400"}`}>
          eSIM {form.has_esim ? "var" : "yoxdur"}
        </button>
        <p className="text-xs text-neutral-500 mt-2">İkon və rəng istəyə görədir. Face ID yalnız X və sonrakı modellərdə olur.</p>
      </div>

      {/* list */}
      <div className="space-y-2">
        {models.map((m) => (
          <div key={m.model_id} className="glass rounded-2xl p-3.5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4" data-testid={`model-row-${m.model_id}`}>
            <div className="flex items-center gap-3 sm:gap-4 min-w-0 sm:flex-1">
            <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
              {m.image_url ? (
                <img src={fileUrl(m.image_url)} alt={m.name} className="w-full h-full object-contain" />
              ) : (
                <Smartphone className="w-5 h-5 text-neutral-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{m.name}</div>
              <div className="text-xs text-neutral-500 flex items-center gap-1.5 flex-wrap mt-0.5">
                <span>{m.category}</span> ·
                {m.storages.map((s) => <span key={s} className="px-1.5 py-0.5 rounded bg-white/5">{storageLabel(s)}</span>)}
                {m.has_faceid && <span className="px-1.5 py-0.5 rounded bg-white/5">Face ID</span>}
                {m.has_esim !== false && <span className="px-1.5 py-0.5 rounded bg-white/5">eSIM</span>}
              </div>
            </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto sm:justify-end sm:max-w-[62%]">
              {m.storages.map((s) => (
                <div key={s} className="flex items-center gap-1">
                  <span className="text-[11px] text-neutral-500 w-9 text-right">{storageLabel(s)}</span>
                  <Input
                    value={editedSP[m.model_id]?.[s] ?? (m.storage_prices?.[String(s)] ?? m.base_price)}
                    onChange={(e) => setSP(m.model_id, s, e.target.value)}
                    type="number" data-testid={`model-price-${m.model_id}-${s}`}
                    className="bg-white/5 border-white/10 w-20 h-9 text-sm" />
                </div>
              ))}
              {(!m.storages || m.storages.length === 0) && (
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-neutral-500 w-12 text-right">Qiymət</span>
                  <Input
                    value={editedSP[m.model_id]?.base ?? m.base_price}
                    onChange={(e) => setSP(m.model_id, "base", e.target.value)}
                    type="number" data-testid={`model-price-${m.model_id}-base`}
                    className="bg-white/5 border-white/10 w-24 h-9 text-sm" />
                </div>
              )}
              {m.has_esim !== false && (
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-[#7fbcff] w-14 text-right">eSIM −₼</span>
                <Input
                  value={editedEsim[m.model_id] ?? (m.esim_diff ?? 0)}
                  onChange={(e) => setEditedEsim((p) => ({ ...p, [m.model_id]: e.target.value }))}
                  type="number" data-testid={`model-esim-${m.model_id}`}
                  className="bg-white/5 border-[#2997FF]/30 w-16 h-9 text-sm" />
              </div>
              )}
              {[["no_reg_diff", "Qeyd"], ["no_beyan_diff", "Bəyan"], ["no_wifi_diff", "WiFi"]].map(([k, lbl]) => (
                <div key={k} className="flex items-center gap-1">
                  <span className="text-[11px] text-[#FF9F0A] w-16 text-right">{lbl} −₼</span>
                  <Input
                    value={editedDiff[m.model_id]?.[k] ?? (m[k] ?? 0)}
                    onChange={(e) => setEditedDiff((p) => ({ ...p, [m.model_id]: { ...(p[m.model_id] || {}), [k]: e.target.value } }))}
                    type="number" data-testid={`model-${k}-${m.model_id}`}
                    className="bg-white/5 border-[#FF9F0A]/30 w-14 h-9 text-sm" />
                </div>
              ))}
              <button onClick={() => savePrice(m)} data-testid={`model-save-${m.model_id}`}
                className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-neutral-300 hover:text-white hover:border-white/30 transition-colors">
                <Save className="w-4 h-4" />
              </button>
              <button onClick={() => remove(m.model_id)} data-testid={`model-delete-${m.model_id}`}
                className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-red-400 hover:bg-red-500/10 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
